import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  useDisclosure,
  useColorModeValue,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton
} from '@chakra-ui/react';
import {
  FaSearch,
  FaPlay,
  FaBook,
  FaChalkboardTeacher,
  FaUserGraduate,
  FaQuoteLeft,
  FaCertificate,
  FaStar,
  FaVolumeUp,
  FaVolumeOff
} from 'react-icons/fa';
import { MdSchool } from 'react-icons/md';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const HomePage = () => {
  const { isOpen: isDemoOpen, onOpen: onDemoOpen, onClose: onDemoClose } = useDisclosure();
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  
  // Colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const buttonSize = useBreakpointValue({ base: 'md', md: 'lg' });

  // Data - Arabic content
  const stats = [
    { number: '1500+', label: 'درس مرئي' },
    { number: '150+', label: 'حصة مباشرة شهرياً' },
    { number: '20+', label: 'أستاذ خبير' },
    { number: '1200+', label: 'طالب مسجل' }
  ];

  const features = [
    { 
      title: 'الدروس المسجلة', 
      description: 'الوصول إلى دروس مرئية متوافقة مع المنهج التونسي',
      icon: FaBook,
      color: 'blue'
    },
    { 
      title: 'الحصص المباشرة', 
      description: 'جلسات تفاعلية مع أساتذة معتمدين',
      icon: FaChalkboardTeacher,
      color: 'green'
    },
    { 
      title: 'الموارد التعليمية', 
      description: 'مواد دراسية شاملة متوافقة مع النظام التعليمي التونسي',
      icon: MdSchool,
      color: 'orange'
    },
    { 
      title: 'التمارين المصححة', 
      description: 'بنك تمارين مع تصحيحات مفصلة',
      icon: FaCertificate,
      color: 'purple'
    }
  ];

  const subjects = [
    { title: 'الرياضيات', level: 'ثانوي/إعدادي', color: 'blue' },
    { title: "الإقتصاد", level: 'ثانوي', color: 'green' },
    { title: 'التصرف', level: 'ثانوي', color: 'orange' },
    { title: 'التاريخ', level: 'ثانوي', color: 'purple' },
    { title: 'الجغرافيا', level: 'ثانوي', color: 'red' },
    { title: 'اللغة الفرنسية', level: 'ثانوي/إعدادي', color: 'pink' },
    { title: 'اللغة الإنجليزية', level: 'ثانوي/إعدادي', color: 'teal' },
    { title: 'اللغة العربية', level: 'إعدادي', color: 'cyan' },
    { title: 'التربية التكنولوجية', level: 'إعدادي', color: 'yellow' },
    { title: 'علوم الحياة والأرض', level: 'إعدادي', color: 'green' },
    { title: 'الفيزياء', level: 'إعدادي', color: 'blue' }
  ];

  const teachers = [
    { name: 'أ. محمد عبيدي', subject: 'الرياضيات', experience: '12 سنة' },
    { name: 'أ. أسامة الوسيفي', subject: 'الإقتصاد و التصرف', experience: '10 سنوات' },
  ];

  const testimonials = [
    { 
      quote: "هذه المنصة غيرت طريقة تعلم أطفالي. الشروحات واضحة والأساتذة مؤهلون جداً.",
      author: "السيدة ليلى م.",
      role: "ولي أمر",
      type: "text"
    },
    { 
      quote: "أنصح جميع الطلاب التونسيين بهذه المنصة. الدروس ممتازة!",
      author: "يوسف ك.",
      role: "طالب باك رياضيات",
      type: "text",
    },
    { 
      quote: "الحصص المباشرة ساعدتني كثيراً في التحضير للامتحان الوطني.",
      author: "أمينة س.",
      role: "طالبة سنة رابعة",
      type: "text"
    },
    { 
      quote: "الأساتذة متمكنون ومتاحون دائماً.",
      author: "كريم ب.",
      role: "طالب سنة ثانية ثانوي",
      type: "text",
    }
  ];

  const faqs = [
    { 
      question: "كيف يمكنني إنشاء حساب؟", 
      answer: "تواصل معنا عبر واتساب أو البريد الإلكتروني أو الهاتف لإنشاء حسابك." 
    },
    { 
      question: "هل يتم تسجيل الحصص المباشرة؟", 
      answer: "نعم، جميع الحصص المباشرة يتم تسجيلها وتتوفر للمراجعة." 
    },
    { 
      question: "هل المحتوى متوافق مع المنهج التونسي؟", 
      answer: "بالطبع، جميع دروسنا تتبع المنهج الرسمي لوزارة التربية التونسية." 
    }
  ];

  const playAudio = (audioSrc) => {
    const audio = new Audio(audioSrc);
    audio.play().catch(e => console.log("Audio play failed:", e));
  };

  const handleVideoClick = (event) => {
    const video = event.target;
    if (isVideoMuted) {
      video.muted = false;
      setIsVideoMuted(false);
    } else {
      video.muted = true;
      setIsVideoMuted(true);
    }
  };

  // Carousel settings
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  return (
    <Box dir="rtl">
      <Header />

      {/* Hero Section */}
      <Box 
        bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`} 
        color="white" 
        py={20} 
        px={4}
        position="relative"
        overflow="hidden"
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <Container maxW="6xl">
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={10} alignItems="center">
              <GridItem>
                <motion.div variants={fadeIn}>
                  <Heading size={headingSize} mb={6} lineHeight="1.2" fontFamily="Cairo, sans-serif">
                   منصة كوبرا :<Box as="span" color={primaryColor}>التميز التعليمي </Box>
                  </Heading>
                  <Text fontSize="xl" mb={8} fontFamily="Cairo, sans-serif">
                    منصة التعلم الإلكتروني للطلاب من الابتدائي إلى البكالوريا في تونس.
                  </Text>
                  <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button 
                        as="a"
                        href="#contact"
                        colorScheme="yellow" 
                        bg={primaryColor}
                        color={secondaryColor}
                        size={buttonSize}
                        fontFamily="Cairo, sans-serif"
                      >
                        إنشاء حساب
                      </Button>
                    </motion.div>
                  </Stack>
                </motion.div>
              </GridItem>
              <GridItem textAlign="center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <Box 
                    position="relative" 
                    borderRadius="lg"
                    boxShadow="2xl"
                    overflow="hidden"
                    display="inline-block"
                    maxW="500px"
                    w="100%"
                  >
                    <video
                      autoPlay
                      muted={isVideoMuted}
                      loop
                      playsInline
                      onClick={handleVideoClick}
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <source src="./assets/cobra-intro.mp4" type="video/mp4" />
                     
                      <Image 
                        src="https://images.unsplash.com/photo-1584697964358-3e14ca57658b" 
                        alt="تلاميذ تونسيون يتعلمون عبر الإنترنت"
                        w="100%"
                        h="auto"
                      />
                    </video>
                    {/* Mute/Unmute indicator */}
                    <Box
                      position="absolute"
                      bottom="4"
                      right="4"
                      bg="rgba(0,0,0,0.7)"
                      color="white"
                      p={2}
                      borderRadius="md"
                      fontSize="sm"
                      fontFamily="Cairo, sans-serif"
                      display="flex"
                      alignItems="center"
                      cursor="pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const video = e.target.closest('div').previousElementSibling;
                        handleVideoClick({ target: video });
                      }}
                      _hover={{ bg: "rgba(0,0,0,0.9)" }}
                      transition="background-color 0.2s"
                    >
                      <Icon as={isVideoMuted ? FaVolumeOff : FaVolumeUp} mr={2} />
                      {isVideoMuted ? 'انقر للصوت' : 'انقر للكتم'}
                    </Box>
                  </Box>
                </motion.div>
              </GridItem>
            </Grid>
          </Container>
        </motion.div>
      </Box>

      {/* Demo Video Modal */}
      <Modal isOpen={isDemoOpen} onClose={onDemoClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontFamily="Cairo, sans-serif">عرض توضيحي للمنصة</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            <Box borderRadius="lg" overflow="hidden">
              <iframe 
                width="100%" 
                height="400" 
                src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Stats Section */}
      <Box py={16}>
        <Container maxW="6xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={10}>
              {stats.map((stat, index) => (
                <motion.div key={index} variants={fadeIn}>
                  <Box 
                    textAlign="center"
                    p={6}
                    bg={cardBg}
                    borderRadius="lg"
                    boxShadow="sm"
                    _hover={{ boxShadow: 'md' }}
                    transition="all 0.3s"
                  >
                    <Text fontSize="3xl" fontWeight="bold" color={primaryColor}>
                      {stat.number}
                    </Text>
                    <Text fontSize="lg" fontFamily="Cairo, sans-serif">{stat.label}</Text>
                  </Box>
                </motion.div>
              ))}
            </SimpleGrid>
          </motion.div>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={16} bg={bgColor}>
        <Container maxW="6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Heading size="xl" mb={12} textAlign="center" color={secondaryColor} fontFamily="Cairo, sans-serif">
              خدماتنا
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Box 
                    bg={cardBg} 
                    p={6} 
                    borderRadius="lg" 
                    boxShadow="md"
                    textAlign="center"
                    borderTop={`4px solid ${primaryColor}`}
                    transition="all 0.3s"
                    _hover={{ boxShadow: 'xl' }}
                    h="100%"
                    minH="250px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Box 
                        bg={`${primaryColor}20`} 
                        w="70px" 
                        h="70px" 
                        borderRadius="full" 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="center"
                        mx="auto"
                        mb={4}
                      >
                        <Icon as={feature.icon} w={8} h={8} color={primaryColor} />
                      </Box>
                      <Heading size="md" mb={3} color={secondaryColor} fontFamily="Cairo, sans-serif">
                        {feature.title}
                      </Heading>
                      <Text color="gray.600" fontFamily="Cairo, sans-serif">
                        {feature.description}
                      </Text>
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </SimpleGrid>
          </motion.div>
        </Container>
      </Box>

      {/* Courses Section - Carousel */}
    
<Box py={16}>
  <Container maxW="6xl">
    <Heading size="xl" mb={12} textAlign="center" color={secondaryColor} fontFamily="Cairo, sans-serif">
      المواد الدراسية
    </Heading>
    
    <Box px={{ base: 2, md: 10 }}>
      <Slider {...settings}>
        {subjects.map((subject, index) => (
          <Box key={index} px={2}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Box 
                bg={cardBg} 
                p={6} 
                borderRadius="lg" 
                boxShadow="md"
                textAlign="center"
                _hover={{ boxShadow: 'xl' }}
                transition="all 0.3s"
                h="100%"
                minH="150px"
              >
                <Heading size="md" mb={2} color={secondaryColor} fontFamily="Cairo, sans-serif">
                  {subject.title}
                </Heading>
                <Badge colorScheme={subject.color} mb={4} fontFamily="Cairo, sans-serif">
                  {subject.level}
                </Badge>
                <br />
           
              </Box>
            </motion.div>
          </Box>
        ))}
      </Slider>
    </Box>
  </Container>
</Box>

      {/* Teachers Section */}
      <Box py={16} bg={bgColor}>
        <Container maxW="6xl">
          <Heading size="xl" mb={12} textAlign="center" color={secondaryColor} fontFamily="Cairo, sans-serif">
            أساتذتنا المتميزون
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            {teachers.map((teacher, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5 }}
              >
                <Box 
                  bg={cardBg} 
                  p={6} 
                  borderRadius="lg" 
                  boxShadow="md"
                  textAlign="center"
                  _hover={{ boxShadow: 'xl' }}
                  transition="all 0.3s"
                >
                  <Box 
                    w="80px" 
                    h="80px" 
                    bg={primaryColor} 
                    borderRadius="full" 
                    mx="auto" 
                    mb={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FaUserGraduate} w={10} h={10} color={secondaryColor} />
                  </Box>
                  <Heading size="md" mb={2} color={secondaryColor} fontFamily="Cairo, sans-serif">
                    {teacher.name}
                  </Heading>
                  <Text color="gray.600" mb={2} fontFamily="Cairo, sans-serif">
                    {teacher.subject}
                  </Text>
                  <Badge colorScheme="green" fontFamily="Cairo, sans-serif">
                    خبرة {teacher.experience}
                  </Badge>
                </Box>
              </motion.div>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box py={16}>
        <Container maxW="6xl">
          <Heading size="xl" mb={12} textAlign="center" color={secondaryColor} fontFamily="Cairo, sans-serif">
            آراء طلابنا وأولياء الأمور
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.02 }}
              >
                <Box 
                  bg={cardBg} 
                  p={6} 
                  borderRadius="lg" 
                  boxShadow="md"
                  position="relative"
                  _hover={{ boxShadow: 'xl' }}
                  transition="all 0.3s"
                >
                  <Icon as={FaQuoteLeft} w={6} h={6} color={primaryColor} mb={4} />
                  <Text mb={4} fontStyle="italic" fontFamily="Cairo, sans-serif">
                    "{testimonial.quote}"
                  </Text>
                  <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="bold" color={secondaryColor} fontFamily="Cairo, sans-serif">
                        {testimonial.author}
                      </Text>
                      <Text fontSize="sm" color="gray.600" fontFamily="Cairo, sans-serif">
                        {testimonial.role}
                      </Text>
                    </VStack>
                    {testimonial.type === 'audio' && (
                      <Button
                        size="sm"
                        colorScheme="yellow"
                        bg={primaryColor}
                        color={secondaryColor}
                        onClick={() => playAudio(testimonial.audioSrc)}
                        leftIcon={<FaVolumeUp />}
                      >
                        استمع
                      </Button>
                    )}
                  </HStack>
                </Box>
              </motion.div>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={16} bg={bgColor}>
        <Container maxW="4xl">
          <Heading size="xl" mb={12} textAlign="center" color={secondaryColor} fontFamily="Cairo, sans-serif">
            الأسئلة الشائعة
          </Heading>
          <Accordion allowToggle>
            {faqs.map((faq, index) => (
              <AccordionItem key={index} border="none" mb={4}>
                <AccordionButton 
                  bg={cardBg} 
                  borderRadius="lg" 
                  p={6}
                  _hover={{ bg: 'gray.50' }}
                  _expanded={{ bg: primaryColor, color: secondaryColor }}
                >
                  <Box flex="1" textAlign="right" fontWeight="bold" fontFamily="Cairo, sans-serif">
                    {faq.question}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4} bg={cardBg} borderRadius="lg" mt={2} fontFamily="Cairo, sans-serif">
                  {faq.answer}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={16} bg={secondaryColor} color="white" id="contact">
        <Container maxW="4xl" textAlign="center">
          <Heading size="xl" mb={6} fontFamily="Cairo, sans-serif">
            ابدأ رحلتك التعليمية اليوم
          </Heading>
          <Text fontSize="lg" mb={8} fontFamily="Cairo, sans-serif">
            انضم إلى آلاف الطلاب الذين يحققون النجاح مع منصة كوبرا التعليمية
          </Text>
          <Stack direction={{ base: 'column', sm: 'row' }} spacing={4} justify="center">
            <Button 
              colorScheme="yellow" 
              bg={primaryColor}
              color={secondaryColor}
              size="lg"
              fontFamily="Cairo, sans-serif"
            >
              تواصل معنا عبر واتساب
            </Button>
            <Button 
              variant="outline" 
              color="white" 
              _hover={{ bg: 'rgba(242, 201, 25, 0.1)' }}
              size="lg"
              fontFamily="Cairo, sans-serif"
            >
              اتصل بنا
            </Button>
          </Stack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default HomePage;